import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faIdCard, 
  faSearch, 
  faCheckSquare, 
  faSquare, 
  faPrint,
  faSpinner,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import api from '../../services/api';
import credencialService from '../../services/credencialService';

// Função para formatar data corretamente (evita problema de fuso horário)
const formatarData = (dataStr) => {
  if (!dataStr) return '-';
  const data = new Date(dataStr);
  const dia = String(data.getUTCDate()).padStart(2, '0');
  const mes = String(data.getUTCMonth() + 1).padStart(2, '0');
  const ano = data.getUTCFullYear();
  return `${dia}/${mes}/${ano}`;
};

const ImprimirCredenciais = () => {
  const navigate = useNavigate();
  
  const [cursos, setCursos] = useState([]);
  const [cursoSelecionado, setCursoSelecionado] = useState('');
  const [filtroNome, setFiltroNome] = useState('');
  const [matriculas, setMatriculas] = useState([]);
  const [matriculasFiltradas, setMatriculasFiltradas] = useState([]);
  const [matriculasSelecionadas, setMatriculasSelecionadas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMatriculas, setLoadingMatriculas] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Carregar cursos
  useEffect(() => {
    carregarCursos();
  }, []);

  const carregarCursos = async () => {
    try {
      const response = await api.get('/cursos');
      setCursos(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar cursos:', error);
      setError('Erro ao carregar lista de cursos');
    }
  };

  // Carregar matrículas quando curso for selecionado
  useEffect(() => {
    if (cursoSelecionado) {
      carregarMatriculas();
    } else {
      setMatriculas([]);
      setMatriculasFiltradas([]);
      setMatriculasSelecionadas([]);
    }
  }, [cursoSelecionado]);

  // Filtrar matrículas localmente quando o filtro de nome mudar
  useEffect(() => {
    if (!filtroNome.trim()) {
      setMatriculasFiltradas(matriculas);
    } else {
      const filtradas = matriculas.filter(m => 
        m.nomeAluno.toLowerCase().includes(filtroNome.toLowerCase())
      );
      setMatriculasFiltradas(filtradas);
    }
  }, [matriculas, filtroNome]);

  const carregarMatriculas = async () => {
    setLoadingMatriculas(true);
    setError('');
    setFiltroNome(''); // Limpar filtro ao recarregar
    
    try {
      const response = await api.get(`/matriculas/curso/${cursoSelecionado}`, {
        params: {
          status: 'Ativa,Concluida' // Apenas matrículas ativas ou concluídas
        }
      });
      
      const data = response.data.data || [];
      setMatriculas(data);
      setMatriculasFiltradas(data);
      setMatriculasSelecionadas([]);
    } catch (error) {
      console.error('Erro ao carregar matrículas:', error);
      setError('Erro ao carregar matrículas do curso');
      setMatriculas([]);
      setMatriculasFiltradas([]);
    } finally {
      setLoadingMatriculas(false);
    }
  };

  const toggleMatricula = (matriculaId) => {
    if (matriculasSelecionadas.includes(matriculaId)) {
      setMatriculasSelecionadas(matriculasSelecionadas.filter(id => id !== matriculaId));
    } else {
      setMatriculasSelecionadas([...matriculasSelecionadas, matriculaId]);
    }
  };

  const toggleTodos = () => {
    const idsFiltradas = matriculasFiltradas.map(m => m.id);
    const todasSelecionadas = idsFiltradas.every(id => matriculasSelecionadas.includes(id));
    
    if (todasSelecionadas) {
      // Remover apenas as filtradas da seleção
      setMatriculasSelecionadas(matriculasSelecionadas.filter(id => !idsFiltradas.includes(id)));
    } else {
      // Adicionar as filtradas que ainda não estão selecionadas
      const novaSelecao = [...new Set([...matriculasSelecionadas, ...idsFiltradas])];
      setMatriculasSelecionadas(novaSelecao);
    }
  };

  const handleGerarCredenciais = async () => {
    if (matriculasSelecionadas.length === 0) {
      setError('Selecione pelo menos uma matrícula');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await credencialService.gerarCredenciais(matriculasSelecionadas);
      
      if (result.success) {
        setSuccess(`Credenciais geradas com sucesso! ${matriculasSelecionadas.length} credencial(is) criada(s).`);
      } else {
        setError(result.message);
      }
    } catch (error) {
      console.error('Erro ao gerar credenciais:', error);
      setError('Erro ao gerar credenciais');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Cabeçalho */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/matriculas')}
              className="mr-4 text-gray-600 hover:text-gray-800"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-800 flex items-center">
                <FontAwesomeIcon icon={faIdCard} className="mr-3 text-green-600" />
                Imprimir Credenciais
              </h1>
              <p className="text-gray-600 mt-1">
                Selecione um curso e as matrículas para gerar credenciais em PDF
              </p>
            </div>
          </div>
        </div>

        {/* Mensagens */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <div className="text-red-800">{error}</div>
            <button onClick={() => setError('')} className="ml-auto text-red-600 hover:text-red-800">
              ×
            </button>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
            <div className="text-green-800">{success}</div>
            <button onClick={() => setSuccess('')} className="ml-auto text-green-600 hover:text-green-800">
              ×
            </button>
          </div>
        )}

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-700">Filtros</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Curso</label>
              <select
                value={cursoSelecionado}
                onChange={(e) => setCursoSelecionado(e.target.value)}
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
              >
                <option value="">Selecione um curso...</option>
                {cursos.map(curso => (
                  <option key={curso.idCurso} value={curso.idCurso}>
                    {curso.nome} - {curso.tipo}
                  </option>
                ))}
              </select>
            </div>

            {cursoSelecionado && matriculas.length > 0 && (
              <div>
                <label className="block text-sm font-medium mb-2">Buscar Aluno</label>
                <div className="relative">
                  <input
                    type="text"
                    value={filtroNome}
                    onChange={(e) => setFiltroNome(e.target.value)}
                    placeholder="Digite o nome do aluno..."
                    className="w-full border rounded px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-green-400"
                  />
                  <FontAwesomeIcon 
                    icon={faSearch} 
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                </div>
              </div>
            )}

            {cursoSelecionado && (
              <div className="flex items-end">
                <button
                  onClick={carregarMatriculas}
                  disabled={loadingMatriculas}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 transition-colors flex items-center justify-center"
                >
                  <FontAwesomeIcon icon={faSearch} className="mr-2" />
                  {loadingMatriculas ? 'Carregando...' : 'Buscar Matrículas'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Lista de Matrículas */}
        {cursoSelecionado && matriculas.length > 0 && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="p-6 border-b flex justify-between items-center bg-gray-50">
              <div>
                <h2 className="text-lg font-semibold text-gray-700">Matrículas Encontradas</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {matriculasSelecionadas.length} selecionada(s) • {matriculasFiltradas.length} de {matriculas.length} exibida(s)
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={toggleTodos}
                  className="px-4 py-2 border rounded hover:bg-gray-100 transition-colors flex items-center"
                >
                  <FontAwesomeIcon 
                    icon={matriculasFiltradas.every(m => matriculasSelecionadas.includes(m.id)) ? faCheckSquare : faSquare} 
                    className="mr-2"
                  />
                  {matriculasFiltradas.every(m => matriculasSelecionadas.includes(m.id)) ? 'Desmarcar Filtrados' : 'Selecionar Filtrados'}
                </button>

                <button
                  onClick={handleGerarCredenciais}
                  disabled={loading || matriculasSelecionadas.length === 0}
                  className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 transition-colors flex items-center font-medium"
                >
                  <FontAwesomeIcon 
                    icon={loading ? faSpinner : faPrint} 
                    className={`mr-2 ${loading ? 'animate-spin' : ''}`}
                  />
                  {loading ? 'Gerando PDF...' : 'Gerar Credenciais'}
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                      <input
                        type="checkbox"
                        checked={matriculasFiltradas.length > 0 && matriculasFiltradas.every(m => matriculasSelecionadas.includes(m.id))}
                        onChange={toggleTodos}
                        className="w-4 h-4"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">Matrícula</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">Aluno</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">CPF</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">Data Matrícula</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {matriculasFiltradas.map((matricula) => (
                    <tr 
                      key={matricula.id}
                      className={`hover:bg-gray-50 cursor-pointer ${
                        matriculasSelecionadas.includes(matricula.id) ? 'bg-green-50' : ''
                      }`}
                      onClick={() => toggleMatricula(matricula.id)}
                    >
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={matriculasSelecionadas.includes(matricula.id)}
                          onChange={() => toggleMatricula(matricula.id)}
                          className="w-4 h-4"
                        />
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {matricula.numeroMatricula}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {matricula.nomeAluno}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {matricula.cpf || '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          matricula.status === 'Ativa' 
                            ? 'bg-green-100 text-green-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {matricula.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {formatarData(matricula.dataMatricula)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Estado vazio */}
        {cursoSelecionado && !loadingMatriculas && matriculas.length === 0 && (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <FontAwesomeIcon icon={faIdCard} className="w-16 h-16 text-gray-300 mb-4" />
            <p className="text-gray-600 text-lg">Nenhuma matrícula encontrada para este curso</p>
            <p className="text-gray-500 text-sm mt-2">
              Apenas matrículas ativas ou concluídas podem gerar credenciais
            </p>
          </div>
        )}

        {!cursoSelecionado && (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <FontAwesomeIcon icon={faIdCard} className="w-16 h-16 text-gray-300 mb-4" />
            <p className="text-gray-600 text-lg">Selecione um curso para começar</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImprimirCredenciais;
